import { Fragment, useEffect, useRef, useState } from "react";

import { InfoOutlined, KeyboardArrowDown, KeyboardArrowUp } from "@mui/icons-material";
import {
  Autocomplete,
  Box,
  FormControl,
  Grid,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { Stack } from "@mui/system";

import { formatDate, formatPrice } from "../../utility/formatters";
import { DeleteIcon } from "../Icons/Icons";
import ResponsiveDataGrid from "../shared/DataGrid/ResponsiveDataGrid";
import { NoteActionButtons, NoteDisplay, NoteInput } from "./NoteManager";
import { accordionSumPnL, offsetPnL, tradePnL } from "./futuresOptionsView";

function gainLossLabel(row, { lot = true, wholeDollars }) {
  const formatted = lot
    ? row.gain_loss_formatted || row.closed_gain_loss_formatted
    : row.closed_gain_loss_formatted;
  if (!formatted) return "";
  if (!wholeDollars) return formatted;
  return formatPrice(lot ? tradePnL(row) : offsetPnL(row), 0);
}

export { accordionSumPnL };

/** Display-only crop profile; click to edit via Autocomplete. */
function CropProfileCell({
  row,
  fetchCropProfilesForRow,
  formatDefaultCropProfile,
  isLoadingCropProfiles,
  handleDispatchRowChange,
}) {
  const [editing, setEditing] = useState(false);
  const display = formatDefaultCropProfile(row) || "";

  if (!editing) {
    return (
      <Box
        onClick={() => setEditing(true)}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.key === "Enter" || e.key === " ") {
            e.preventDefault();
            setEditing(true);
          }
        }}
        sx={{
          mb: 1,
          pr: 1,
          width: "100%",
          minHeight: 36,
          display: "flex",
          alignItems: "center",
          cursor: "pointer",
          borderRadius: 1,
          px: 0.5,
          "&:hover": { backgroundColor: "action.hover" },
        }}
        data-testid="crop-profile-display"
      >
        <Typography variant="body2" color={display ? "text.primary" : "text.secondary"} noWrap>
          {display || "—"}
        </Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ mb: 1, pr: 1, width: "100%" }}>
      <FormControl fullWidth>
        <Autocomplete
          name="crop_profile"
          options={fetchCropProfilesForRow() || []}
          multiple={false}
          loading={isLoadingCropProfiles}
          value={formatDefaultCropProfile(row) || null}
          openOnFocus
          autoFocus
          onChange={(event, value) => {
            if (row.is_combined) {
              (row.lots || []).forEach((lot) => handleDispatchRowChange(lot.id, "crop_profile", value));
            } else {
              handleDispatchRowChange(row.id, "crop_profile", value);
            }
            setEditing(false);
          }}
          onClose={() => setEditing(false)}
          autoHighlight
          renderInput={(params) => (
            <TextField
              name="crop_profile"
              {...params}
              required
              onChange={null}
              InputLabelProps={{ shrink: true }}
              autoFocus
            />
          )}
          renderOption={(props, option) => {
            const { key, ...optionProps } = props;
            return (
              <li key={key} {...optionProps}>
                <Box>{option}</Box>
              </li>
            );
          }}
        />
      </FormControl>
    </Box>
  );
}

/** Shared renderer for lot / offset / legacy child rows (dark rows under an accordion parent). */
function NestedTradeRow({
  row,
  kind,
  noteKey,
  wholeDollars,
  handleToggleNoteVisibility,
  handleSaveNote,
  handleDeleteTransactionOnClick,
}) {
  const isLot = kind === "lot";
  return (
    <TableRow
      className="tablerow-dark"
      data-testid={`futures-options-row-${row.tracer_number}`}
      sx={isLot ? undefined : { opacity: 0.92 }}
    >
      <TableCell>&nbsp;</TableCell>
      <TableCell sx={isLot ? undefined : { pl: 3, color: "text.secondary" }}>{row.trade_date_formatted}</TableCell>
      <TableCell className="org-qty" sx={{ textAlign: "right", fontWeight: "lighter" }}>
        {row.orig_qty_formatted}
      </TableCell>
      <TableCell sx={{ textAlign: "right" }}>{isLot ? row.accumulated_formatted : <>&nbsp;</>}</TableCell>
      <TableCell className="open-qty" sx={{ textAlign: "right" }}>
        {row.open_qty_formatted}
      </TableCell>
      <TableCell sx={{ textAlign: "right" }}>{isLot ? row.potential_remaining_formatted : <>&nbsp;</>}</TableCell>
      <TableCell sx={{ textTransform: "capitalize" }}>
        {row.security_desc_line_1?.toLowerCase()}
        {Boolean(row?.option_expire_date) && (
          <>
            <br />
            {row.option_expire_date_formatted}
          </>
        )}
        {Boolean(row?.option_strike_price) && (
          <>
            <br />
            {row.price_formatted}
          </>
        )}
      </TableCell>
      <TableCell sx={{ textAlign: "right" }}>{row.price_formatted}</TableCell>
      <TableCell sx={{ textAlign: "right" }}>{row.current_price_formatted}</TableCell>
      <TableCell className="gain-loss" sx={{ textAlign: "right" }}>
        {gainLossLabel(row, { lot: isLot, wholeDollars })}
      </TableCell>
      <TableCell>
        <div className="no-print">
          <NoteActionButtons
            showNote={row.showNote}
            hasNote={Boolean(row.note)}
            onToggleNote={() => handleToggleNoteVisibility(row.id)}
            onSaveNote={() => handleSaveNote(row.id, noteKey)}
            size="small"
          />
        </div>
      </TableCell>
      <TableCell>{row.tracer_number}</TableCell>
      <TableCell className="no-print">&nbsp;</TableCell>
      <TableCell className="no-print">
        {isLot ? (
          <IconButton onClick={() => handleDeleteTransactionOnClick(row)}>
            <DeleteIcon />
          </IconButton>
        ) : (
          <>&nbsp;</>
        )}
      </TableCell>
      <TableCell />
    </TableRow>
  );
}

export default function FuturesOptionsTable({
  currentCountyPractice,
  handleToggleNoteVisibility,
  handleDispatchRowChange,
  handleDeleteTransactionOnClick,
  fetchCropProfilesForRow,
  formatDefaultCropProfile,
  isLoadingCropProfiles,
  setSelectedOTCRow,
  data = [],
  totals = {
    total_orig_qty_formatted: "",
    total_open_qty_formatted: "",
    total_gain_loss_formatted: "",
    total_gain_loss: 0,
  },
  sectionKey,
  totalLabel = "Total",
}) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const wholeDollars = useMediaQuery(theme.breakpoints.down("lg"));
  const noteInputRefs = useRef({});
  const [noteValues, setNoteValues] = useState({});
  const [expandedGroups, setExpandedGroups] = useState({});

  const toggleGroup = (groupId) => {
    if (!groupId) return;
    setExpandedGroups((prev) => {
      const next = { ...prev };
      if (next[groupId]) delete next[groupId];
      else next[groupId] = true;
      return next;
    });
  };

  const accordionGroupId = (row) => {
    if (!row?.is_accordion) return null;
    if (row.is_combined) {
      return `combined-${(row.lots || []).map((lot) => lot.id).join("_")}`;
    }
    return `single-${row.id}`;
  };

  const getRef = (refKey) => {
    if (!noteInputRefs.current[refKey]) {
      noteInputRefs.current[refKey] = { current: null };
    }
    return noteInputRefs.current[refKey];
  };

  const handleNoteInputChange = (refKey, event) => {
    setNoteValues((prev) => ({
      ...prev,
      [refKey]: event.target.value,
    }));
  };

  const handleSaveNote = async (id, rowKey) => {
    const refKey = typeof rowKey === "string" && rowKey.includes("-sub-") ? rowKey : `${id}-${rowKey}`;
    const currentValue = noteValues[refKey] || "";
    handleDispatchRowChange(id, "note", currentValue);
    handleToggleNoteVisibility(id, rowKey);
  };

  const handleKeyDown = async (e, id, rowKey) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      await handleSaveNote(id, rowKey);
    }
  };

  useEffect(() => {
    if (data && data.length > 0) {
      setNoteValues((prev) => {
        const newNoteValues = { ...prev };
        let hasChanges = false;

        data.forEach((row, index) => {
          const refKey = `${row.id}-${index}`;
          if (row.showNote && !newNoteValues[refKey]) {
            newNoteValues[refKey] = row.note || "";
            hasChanges = true;
          } else if (row.note && !newNoteValues[refKey]) {
            newNoteValues[refKey] = row.note || "";
            hasChanges = true;
          }

          if (row.subdata && row.subdata.length > 0) {
            row.subdata.forEach((subRow, subIndex) => {
              const subRowKey = `${subRow.id}-sub-${subIndex}`;
              if (subRow.showNote && !newNoteValues[subRowKey]) {
                newNoteValues[subRowKey] = subRow.note || "";
                hasChanges = true;
              } else if (subRow.note && !newNoteValues[subRowKey]) {
                newNoteValues[subRowKey] = subRow.note || "";
                hasChanges = true;
              }
            });
          }
        });

        return hasChanges ? newNoteValues : prev;
      });
    }
  }, [data]);

  const mobileColumns = [
    { field: "position", headerName: "Position" },
    { field: "trade_date_formatted", headerName: "Date" },
    { field: "orig_qty_formatted", headerName: "Orig. Qty" },
    { field: "accumulated_formatted", headerName: "Accumulated" },
    { field: "open_qty_formatted", headerName: "Open Qty" },
    { field: "potential_remaining_formatted", headerName: "Potential Remaining", hideInSubRow: true },
    { field: "contract_formatted", headerName: "Contract" },
    { field: "price_formatted", headerName: "Price" },
    {
      field: "current_price_formatted",
      headerName: "Current Price",
      renderCell: ({ row }) => <abbr title={row.current_price_context}>{row.current_price_formatted || "-"}</abbr>,
    },
    {
      field: "gain_loss_formatted",
      headerName: "Gain/Loss",
      renderCell: ({ row }) => gainLossLabel(row, { wholeDollars }) || "-",
    },
    {
      field: "note_control",
      headerName: "Note",
      renderCell: ({ row, index }) => (
        <NoteActionButtons
          showNote={row.showNote}
          hasNote={Boolean(row.note)}
          onToggleNote={() => handleToggleNoteVisibility(row.id)}
          onSaveNote={() => handleSaveNote(row.id, index)}
        />
      ),
    },
    {
      field: "note_edit",
      headerName: "",
      combine: true,
      fullWidth: (params) => params.row.showNote,
      renderCell: ({ row, index }) => (
        <>
          {row.showNote ? (
            <NoteInput
              note={row.note || ""}
              inputRef={getRef(`${row.id}-${index}`)}
              onChange={(event) => handleNoteInputChange(`${row.id}-${index}`, event)}
            />
          ) : (
            row.note && (
              <Box sx={{ minHeight: "2rem" }}>
                <NoteDisplay note={row.note} onEdit={() => handleToggleNoteVisibility(row.id)} showLabel={false} />
              </Box>
            )
          )}
        </>
      ),
    },
    { field: "tracer_number", headerName: "Tracer" },
    {
      field: "crop-profile",
      headerName: "Crop Profile",
      hideInSubRow: true,
      renderCell: ({ row }) => (
        <CropProfileCell
          row={row}
          fetchCropProfilesForRow={fetchCropProfilesForRow}
          formatDefaultCropProfile={formatDefaultCropProfile}
          isLoadingCropProfiles={isLoadingCropProfiles}
          handleDispatchRowChange={handleDispatchRowChange}
        />
      ),
    },
    {
      field: "product_description",
      headerName: "",
      hideInSubRow: true,
      fullWidth: true,
      combine: true,
      renderCell: ({ row }) =>
        row.is_otc ? (
          <Box>
            <IconButton className="no-print" onClick={() => setSelectedOTCRow(row)} size="small">
              <InfoOutlined />
            </IconButton>
            <Typography variant="body2" display="block" color="text.secondary">
              {row.product_description}
            </Typography>
          </Box>
        ) : null,
    },
    {
      field: "delete",
      headerName: "Delete",
      hideInSubRow: true,
      renderCell: ({ row }) => (
        <IconButton onClick={() => handleDeleteTransactionOnClick(row)}>
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  const [paginationModel, setPaginationModel] = useState({
    pageSize: 5,
    page: 0,
  });

  const [sortModel, setSortModel] = useState([]);

  return isMobile ? (
    <>
      <ResponsiveDataGrid
        rows={data}
        columns={mobileColumns}
        loading={isLoadingCropProfiles}
        paginationModel={paginationModel}
        onPaginationModelChange={setPaginationModel}
        sortModel={sortModel}
        onSortModelChange={setSortModel}
        pageSizeOptions={[5, 10, 25, 50, 100]}
      />
      {data.length > 0 && (
        <>
          <Typography sx={{ mb: 2, fontWeight: "bold", fontSize: "1.125rem" }}>Totals</Typography>
          <Grid container sx={{ backgroundColor: "grey.100", p: 1 }} justifyItems="flex-start" spacing={1}>
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Orig. Qty</Typography>
                <Typography>{totals.total_orig_qty_formatted}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Open Qty</Typography>
                <Typography>{totals.total_open_qty_formatted}</Typography>
              </Stack>
            </Grid>
            <Grid item xs={12}>
              <Stack direction="row" justifyContent="space-between">
                <Typography>Gain/Loss</Typography>
                <Typography>
                  {wholeDollars ? formatPrice(totals.total_gain_loss, 0) : totals.total_gain_loss_formatted}
                </Typography>
              </Stack>
            </Grid>
          </Grid>
        </>
      )}
    </>
  ) : (
    <Table
      data-testid={`futures-options-table-${sectionKey || currentCountyPractice?.commodity_name?.toLowerCase()}`}
      sx={{
        // 15 cells × MUI's 16px/side padding was 480px of the table's width; tighten so it fits without inner scroll.
        "& th, & td": { px: 0.75 },
        "@media print": {
          tableLayout: "auto",
          width: "100%",
          fontSize: "0.65rem",
          "& th, & td": {
            padding: "2px 4px",
            whiteSpace: "normal",
            wordBreak: "break-word",
            hyphens: "auto",
            lineHeight: 1.2,
            fontSize: "0.65rem",
            pageBreakInside: "avoid",
          },
          "& th": {
            fontWeight: "bold",
            whiteSpace: "normal",
            verticalAlign: "top",
          },
          "& .no-print": {
            display: "none",
          },
          "& th:nth-of-type(1)": { width: "auto", minWidth: "50px" }, // Position
          "& th:nth-of-type(2)": { width: "auto", minWidth: "40px" }, // Date
          "& th:nth-of-type(3)": { width: "auto", minWidth: "50px" }, // Orig. Qty
          "& th:nth-of-type(4)": { width: "auto", minWidth: "50px" }, // Accumulated
          "& th:nth-of-type(5)": { width: "auto", minWidth: "50px" }, // Open Qty
          "& th:nth-of-type(6)": { width: "auto", minWidth: "50px" }, // Potential Remaining
          "& th:nth-of-type(7)": { width: "auto", minWidth: "60px" }, // Contract
          "& th:nth-of-type(8)": { width: "auto", minWidth: "50px" }, // Price
          "& th:nth-of-type(9)": { width: "auto", minWidth: "50px" }, // Current Price
          "& th:nth-of-type(10)": { width: "auto", minWidth: "50px" }, // Gain/Loss
          "& th:nth-of-type(11)": { width: "auto", minWidth: "40px" }, // Note
          "& th:nth-of-type(12)": { width: "auto", minWidth: "40px" }, // Tracer
        },
      }}
    >
      <TableHead>
        <TableRow>
          <TableCell sx={{ fontWeight: "bold" }}>Position</TableCell>
          <TableCell sx={{ fontWeight: "bold" }}>Date</TableCell>
          <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>Orig. Qty</TableCell>
          <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>Accumulated</TableCell>
          <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>Open Qty</TableCell>
          <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
            Potential
            <br />
            Remaining
          </TableCell>
          <TableCell sx={{ fontWeight: "bold" }}>Contract</TableCell>
          <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>Price</TableCell>
          <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
            Current
            <br />
            Price
          </TableCell>
          <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>
            Gain/
            <br />
            Loss
          </TableCell>
          <TableCell sx={{ fontWeight: "bold", minWidth: 40, maxWidth: 80 }}>Note</TableCell>
          <TableCell sx={{ fontWeight: "bold", minWidth: 40, maxWidth: 80 }}>Tracer</TableCell>
          <TableCell sx={{ fontWeight: "bold" }} className="no-print">
            Crop Profile
          </TableCell>
          <TableCell className="no-print"></TableCell>
          <TableCell sx={{ width: 40 }} aria-label="Expand" />
        </TableRow>
      </TableHead>

      <TableBody>
        {data.map((row, index) => {
          const groupId = accordionGroupId(row);
          const isExpanded = Boolean(groupId && expandedGroups[groupId]);
          return (
            <Fragment key={groupId || `${row.id}-${index}`}>
              <TableRow
                className="tablerow-light"
                data-testid={
                  row.is_accordion
                    ? `futures-options-group-${index}`
                    : `futures-options-row-${row.tracer_number}`
                }
                sx={{
                  "& td": { borderBottom: row.note || row.showNote || row.product_description ? "none" : undefined },
                }}
              >
                <TableCell>{row.position}</TableCell>
                <TableCell sx={row.is_combined ? { color: "text.secondary" } : undefined}>
                  {row.trade_date_formatted}
                </TableCell>
                <TableCell className="org-qty" sx={{ textAlign: "right", fontWeight: "lighter" }}>
                  {row.orig_qty_formatted}
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>{row.accumulated_formatted}</TableCell>
                <TableCell className="open-qty" sx={{ textAlign: "right" }}>
                  {row.open_qty_formatted}
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>{row.potential_remaining_formatted}</TableCell>
                <TableCell sx={{ textTransform: "capitalize", width: "6%", whiteSpace: "normal !important" }}>
                  {row.security_desc_line_1?.toLowerCase()}
                  {Boolean(row?.option_expire_date) && (
                    <>
                      <br />
                      Exp {formatDate(row.option_expire_date)}
                    </>
                  )}
                  {Boolean(row?.option_strike_price) && (
                    <>
                      <br />
                      {row.price_formatted}
                    </>
                  )}
                </TableCell>
                <TableCell sx={{ textAlign: "right" }}>{row.price_formatted}</TableCell>
                <TableCell className="current-price" sx={{ textAlign: "right" }}>
                  <abbr title={row.current_price_context}>{row.current_price_formatted}</abbr>
                </TableCell>
                <TableCell className="gain-loss" sx={{ textAlign: "right" }}>
                  {/* Collapsed → sum on parent; expanded → blank (G/L on detail lines only). */}
                  {row.is_accordion
                    ? isExpanded
                      ? ""
                      : formatPrice(accordionSumPnL(row), wholeDollars ? 0 : 2)
                    : gainLossLabel(row, { wholeDollars })}
                </TableCell>
                <TableCell>
                  <Box sx={{ mb: 1, pr: 1, width: "100%", display: "flex", alignItems: "center", gap: 1 }}>
                    {!row.is_combined && (
                      <div className="no-print">
                        <NoteActionButtons
                          showNote={row.showNote}
                          hasNote={Boolean(row.note)}
                          onToggleNote={() => handleToggleNoteVisibility(row.id)}
                          onSaveNote={() => handleSaveNote(row.id, index)}
                          size="small"
                        />
                      </div>
                    )}
                    {row.is_otc && (
                      <IconButton className="no-print" onClick={() => setSelectedOTCRow(row)} size="small">
                        <InfoOutlined />
                      </IconButton>
                    )}
                  </Box>
                </TableCell>
                <TableCell>{!row.is_combined ? row.tracer_number : ""}</TableCell>
                <TableCell sx={{ p: "0px !important" }} className="no-print">
                  <CropProfileCell
                    row={row}
                    fetchCropProfilesForRow={fetchCropProfilesForRow}
                    formatDefaultCropProfile={formatDefaultCropProfile}
                    isLoadingCropProfiles={isLoadingCropProfiles}
                    handleDispatchRowChange={handleDispatchRowChange}
                  />
                </TableCell>
                <TableCell className="no-print">
                  {!row.is_combined && (
                    <IconButton onClick={() => handleDeleteTransactionOnClick(row)}>
                      <DeleteIcon />
                    </IconButton>
                  )}
                </TableCell>
                <TableCell sx={{ width: 40, textAlign: "right" }}>
                  {row.is_accordion && (
                    <IconButton
                      type="button"
                      size="small"
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        toggleGroup(groupId);
                      }}
                      aria-expanded={isExpanded}
                      aria-label={isExpanded ? "Collapse lots" : "Expand lots"}
                      data-testid={`futures-options-expand-${index}`}
                    >
                      {isExpanded ? <KeyboardArrowUp /> : <KeyboardArrowDown />}
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>

              {/* Add notes row */}
              {row.note && !row.showNote && (
                <TableRow className="tablerow-light" sx={{ "& td": { border: "none" } }}>
                  <TableCell></TableCell>
                  <TableCell
                    colSpan={14}
                    sx={{
                      fontSize: "0.75rem",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      padding: "4px 16px",
                      fontWeight: "normal",
                    }}
                  >
                    <NoteDisplay note={row.note} onEdit={() => handleToggleNoteVisibility(row.id)} showLabel />
                  </TableCell>
                </TableRow>
              )}

              {/* Add notes edit row */}
              {row.showNote && (
                <TableRow className="tablerow-light" sx={{ "& td": { border: "none" } }}>
                  <TableCell></TableCell>
                  <TableCell
                    colSpan={14}
                    sx={{
                      fontSize: "0.75rem",
                      whiteSpace: "normal",
                      wordBreak: "break-word",
                      padding: "4px 16px",
                      fontWeight: "normal",
                    }}
                  >
                    <NoteInput
                      note={row.note || ""}
                      placeholder="Notes"
                      maxLength={100}
                      inputRef={getRef(`${row.id}-${index}`)}
                      onChange={(event) => {
                        handleNoteInputChange(`${row.id}-${index}`, event);
                      }}
                      onKeyDown={(e) => handleKeyDown(e, row.id, index)}
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          fontSize: "0.75rem",
                          padding: "4px 8px",
                          fontWeight: "normal",
                        },
                        "& .MuiInputBase-input": {
                          fontSize: "0.75rem",
                        },
                      }}
                    />
                  </TableCell>
                </TableRow>
              )}

              {/* Add product description row */}
              {row.is_otc && (
                <>
                  <TableRow className="tablerow-light">
                    <TableCell></TableCell>
                    <TableCell
                      colSpan={14}
                      sx={{
                        fontSize: "0.75rem",
                        whiteSpace: "normal",
                        wordBreak: "break-word",
                        padding: "4px 16px",
                        fontWeight: "normal",
                      }}
                    >
                      <Typography variant="body2" display="block" color="text.secondary">
                        {row.product_description}
                      </Typography>
                    </TableCell>
                  </TableRow>
                </>
              )}

              {/* Combined group accordion: each original trade, then its nested offsets */}
              {row.is_combined &&
                isExpanded &&
                row.lots.map((lotRow, lotIndex) => {
                  const lotKey = `${lotRow.id}-lot-${lotIndex}`;
                  return (
                    <Fragment key={lotKey}>
                      <NestedTradeRow
                        row={lotRow}
                        kind="lot"
                        wholeDollars={wholeDollars}
                        noteKey={lotKey}
                        handleToggleNoteVisibility={handleToggleNoteVisibility}
                        handleSaveNote={handleSaveNote}
                        handleDeleteTransactionOnClick={handleDeleteTransactionOnClick}
                      />
                      {(lotRow.offsets || []).map((offsetRow, offsetIndex) => {
                        const offsetKey = `${offsetRow.id}-offset-${lotIndex}-${offsetIndex}`;
                        return (
                          <NestedTradeRow
                            key={offsetKey}
                            row={offsetRow}
                            kind="offset"
                            wholeDollars={wholeDollars}
                            noteKey={offsetKey}
                            handleToggleNoteVisibility={handleToggleNoteVisibility}
                            handleSaveNote={handleSaveNote}
                            handleDeleteTransactionOnClick={handleDeleteTransactionOnClick}
                          />
                        );
                      })}
                    </Fragment>
                  );
                })}

              {/* Single-trade accordion (closed / has offsets): offsets nest under the original trade */}
              {row.is_accordion &&
                !row.is_combined &&
                isExpanded &&
                (row.offsets || []).map((offsetRow, offsetIndex) => {
                  const offsetKey = `${offsetRow.id}-offset-${offsetIndex}`;
                  return (
                    <NestedTradeRow
                      key={offsetKey}
                      row={offsetRow}
                      kind="offset"
                      wholeDollars={wholeDollars}
                      noteKey={offsetKey}
                      handleToggleNoteVisibility={handleToggleNoteVisibility}
                      handleSaveNote={handleSaveNote}
                      handleDeleteTransactionOnClick={handleDeleteTransactionOnClick}
                    />
                  );
                })}

              {/* Legacy always-open offset subrows — only when not using accordion */}
              {!row.is_accordion &&
                row.subdata.map((subRow, subIndex) => {
                  const subRowKey = `${subRow.id}-sub-${subIndex}`;
                  return (
                    <Fragment key={subRowKey}>
                      <NestedTradeRow
                        row={subRow}
                        kind="offset"
                        wholeDollars={wholeDollars}
                        noteKey={subRowKey}
                        handleToggleNoteVisibility={handleToggleNoteVisibility}
                        handleSaveNote={handleSaveNote}
                        handleDeleteTransactionOnClick={handleDeleteTransactionOnClick}
                      />

                      {subRow.note && !subRow.showNote && (
                        <TableRow className="tablerow-dark" sx={{ "& td": { border: "none" } }}>
                          <TableCell></TableCell>
                          <TableCell
                            colSpan={14}
                            sx={{
                              fontSize: "0.75rem",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              padding: "4px 16px",
                              fontWeight: "normal",
                            }}
                          >
                            <NoteDisplay
                              note={subRow.note}
                              onEdit={() => handleToggleNoteVisibility(subRow.id)}
                              showLabel
                            />
                          </TableCell>
                        </TableRow>
                      )}

                      {subRow.showNote && (
                        <TableRow className="tablerow-dark" sx={{ "& td": { border: "none" } }}>
                          <TableCell></TableCell>
                          <TableCell
                            colSpan={14}
                            sx={{
                              fontSize: "0.75rem",
                              whiteSpace: "normal",
                              wordBreak: "break-word",
                              padding: "4px 16px",
                              fontWeight: "normal",
                            }}
                          >
                            <NoteInput
                              note={subRow.note || ""}
                              placeholder="Notes"
                              maxLength={100}
                              inputRef={getRef(subRowKey)}
                              onChange={(event) => {
                                handleNoteInputChange(subRowKey, event);
                              }}
                              onKeyDown={(e) => handleKeyDown(e, subRow.id, subRowKey)}
                              sx={{
                                "& .MuiOutlinedInput-root": {
                                  fontSize: "0.75rem",
                                  padding: "4px 8px",
                                  fontWeight: "normal",
                                },
                                "& .MuiInputBase-input": {
                                  fontSize: "0.75rem",
                                },
                              }}
                            />
                          </TableCell>
                        </TableRow>
                      )}
                    </Fragment>
                  );
                })}
            </Fragment>
          );
        })}

        {/* Section P&L total */}
        <TableRow>
          <TableCell colSpan={2} sx={{ fontWeight: "bold" }}>
            {totalLabel}
          </TableCell>
          <TableCell sx={{ textAlign: "right", fontWeight: "lighter" }}>{totals.total_orig_qty_formatted}</TableCell>
          <TableCell sx={{ fontWeight: "bold" }}>&nbsp;</TableCell>
          <TableCell sx={{ textAlign: "right", fontWeight: "bold" }}>{totals.total_open_qty_formatted}</TableCell>
          <TableCell colSpan={4}></TableCell>
          <TableCell sx={{ textAlign: "right", fontWeight: "bold" }} data-testid={`futures-options-pnl-${sectionKey || "all"}`}>
            {wholeDollars ? formatPrice(totals.total_gain_loss, 0) : totals.total_gain_loss_formatted}
          </TableCell>
          <TableCell colSpan={5}></TableCell>
        </TableRow>
      </TableBody>
    </Table>
  );
}
